import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bike, Bus, Car, CarFront, FileText, History, Link2, Truck } from 'lucide-react';

import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { StatusBadge } from '@/components/ui/status-badge';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { useEntityModalContext } from '@/contexts/EntityModalContext';
import type { VehicleViewModel } from '@/graphql/generated';
import { useOne } from '@/hooks/resourceQueries';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { getVehicleMakeLogoUrl } from '@/hooks/useVehicleMakesModels';

import { VEHICLE_QUERY } from '../vehicles.gql.tsx';
import { VehicleContracts } from './vehicle-contracts';
import { VehicleHeaderCompactMeta } from './vehicle-header-compact-meta';
import { VehicleReferencesTab } from './vehicle-references-tab';
import { VehicleParkingSessions } from './vehicle-parking-sessions';

interface VehicleDetailsProps {
	vehicleId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

const resolveVehicleIcon = (vehicleTypeName?: string | null) => {
	const normalizedType = vehicleTypeName?.toLowerCase() ?? '';

	if (normalizedType.includes('motor')) {
		return Bike;
	}

	if (normalizedType.includes('truck') || normalizedType.includes('lorry')) {
		return Truck;
	}

	if (normalizedType.includes('bus')) {
		return Bus;
	}

	return CarFront;
};

interface VehicleHeaderData {
	vehicleId: string;
	plateNumber?: string | null;
	isActive: boolean;
	color?: string | null;
	make?: string | null;
	model?: string | null;
	vehicleType?: { name?: string | null } | null;
	customer?: {
		customerId?: string | null;
		displayName?: string | null;
		isCompany?: boolean | null;
	} | null;
}

export const VehicleDetailsHeader = ({
	vehicle,
	onOwnerClick,
	className,
	...actions
}: {
	vehicle: VehicleHeaderData;
	onOwnerClick?: (entityType: string, entityId: string) => void;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t: tV } = useTranslation('vehicles');
	const [logoError, setLogoError] = useState(false);
	const logoUrl = getVehicleMakeLogoUrl(vehicle.make);
	const customerDisplayName = vehicle.customer?.displayName?.trim() ?? null;
	const customerTypeLabel = vehicle.customer
		? vehicle.customer.isCompany
			? tV('details.company')
			: tV('details.individual')
		: null;
	const displayName = vehicle.plateNumber || tV('details.unnamedVehicle');
	const VehicleHeaderIcon = resolveVehicleIcon(vehicle.vehicleType?.name);

	useEffect(() => {
		setLogoError(false);
	}, [logoUrl]);

	return (
		<EntityDetailsHeader
			icon={
				<div
					className={`flex items-center justify-center w-14 h-14 rounded-full bg-transparent flex-shrink-0 overflow-hidden ${
						logoUrl && !logoError
							? ''
							: 'border-2 border-gray-400 dark:border-gray-500'
					}`}
				>
					{logoUrl && !logoError ? (
						<img
							src={logoUrl}
							alt={vehicle.make ? `${vehicle.make} logo` : 'Vehicle logo'}
							className="h-full w-full object-cover"
							onError={() => setLogoError(true)}
						/>
					) : (
						<VehicleHeaderIcon className="w-7 h-7 text-gray-600 dark:text-gray-300" />
					)}
				</div>
			}
			name={displayName}
			statusBadge={<StatusBadge isActive={vehicle.isActive} />}
			subtitle={
				<VehicleHeaderCompactMeta
					color={vehicle.color}
					make={vehicle.make}
					model={vehicle.model}
					ownerName={customerDisplayName}
					ownerId={vehicle.customer?.customerId ?? null}
					ownerTypeLabel={customerTypeLabel}
					onOwnerClick={onOwnerClick ?? (() => {})}
				/>
			}
			entityId={actions.entityId ?? vehicle.vehicleId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

export const VehicleDetails = ({
	vehicleId,
	activeTab: externalActiveTab,
	onTabChange: externalOnTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: VehicleDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tV } = useTranslation('vehicles');
	const { openEntityModal } = useEntityModalContext();
	const [internalActiveTab, setInternalActiveTab] = useState('parkingSessions');
	const [hasOpenedReferences, setHasOpenedReferences] = useState(
		externalActiveTab === 'references'
	);

	const normalizeTab = (tab: string) =>
		tab === 'details' ? 'parkingSessions' : tab;

	// Use external control if provided, otherwise use internal state
	const activeTab = externalActiveTab
		? normalizeTab(externalActiveTab)
		: internalActiveTab;
	const setActiveTab = externalOnTabChange
		? (tab: string) => externalOnTabChange(normalizeTab(tab))
		: (tab: string) => setInternalActiveTab(normalizeTab(tab));

	const handleTabChange = (tab: string) => {
		if (tab === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
		setActiveTab(tab);
	};

	useEffect(() => {
		if (activeTab === 'references' && !hasOpenedReferences) {
			setHasOpenedReferences(true);
		}
	}, [activeTab, hasOpenedReferences]);

	const { data, isLoading, isError } = useOne<VehicleViewModel>({
		resource: 'vehicles',
		id: vehicleId,
		meta: {
			gqlQuery: VEHICLE_QUERY,
			dataPath: 'vehicle'
		}
	});

	const { shouldShowLoading } = useDelayedLoading(isLoading, {
		delay: 300,
		showInitialLoading: false
	});

	const vehicle = data?.data;
	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-32 h-4 rounded animate-pulse bg-muted" />
				<div className="w-20 h-4 rounded animate-pulse bg-muted" />
				<div className="space-y-2">
					<div className="w-full h-3 rounded animate-pulse bg-muted" />
					<div className="w-3/4 h-3 rounded animate-pulse bg-muted" />
				</div>
			</div>
		);
	}

	if (!isLoading && (isError || !vehicle)) {
		return (
			<div className="py-8 text-sm text-center text-muted-foreground">
				{tV('details.loadError')}
			</div>
		);
	}

	if (!vehicle) {
		return null;
	}

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<VehicleDetailsHeader
					vehicle={vehicle}
					onOwnerClick={openEntityModal}
					entityId={vehicleId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

			<Tabs
				value={activeTab}
				onValueChange={handleTabChange}
				className="flex flex-col h-full min-h-0"
			>
				{/* Tab Bar - fixed height at top */}
				{!hideTabsList && (
					<div className="flex-shrink-0 pb-2">
						<TabsList>
							<TabLink value="parkingSessions" icon={Car} label={tV('tabs.parkingSessions')} />
							<TabLink value="contracts" icon={FileText} label={t('tabs.contracts')} />
							<TabLink value="references" icon={Link2} label={t('tabs.references')} />
							<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
						</TabsList>
					</div>
				)}

				{/* Tab Content - scrollable area with min-height for stable modal sizing */}
				<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
					<TabsContent value="parkingSessions" className="mt-0">
						<VehicleParkingSessions
							vehicleId={vehicleId}
							isActive={activeTab === 'parkingSessions'}
						/>
					</TabsContent>

					<TabsContent value="contracts" className="mt-0">
						<VehicleContracts
							vehicleId={vehicleId}
							isActive={activeTab === 'contracts'}
						/>
					</TabsContent>

					<TabsContent value="references" className="mt-0">
						{hasOpenedReferences && (
							<VehicleReferencesTab vehicleId={vehicleId} />
						)}
					</TabsContent>

					<TabsContent value="audit" className="mt-0">
						<AuditTab
							data={{
								createdAt: vehicle.createdAt,
								activatedAt: vehicle.activatedAt,
								deactivatedAt: vehicle.deactivatedAt
							}}
						/>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
};
